import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, Tooltip, LayersControl, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth, API } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  LogOut, MapPin, FileText, Phone, ShieldAlert, AlertCircle, Loader2, Building2,
  Crosshair, Save, X,
} from 'lucide-react';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import NavCluster from '../components/NavCluster';
import { toast } from 'sonner';

const RISK_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

// Default marker icon fix (Leaflet has webpack issues with default icons)
const PICK_ICON = L.divIcon({
  className: 'custom-pick-marker',
  html: '<div style="background:#1a4d80;border:3px solid #fff;border-radius:50%;width:24px;height:24px;box-shadow:0 0 0 2px #1a4d80,0 2px 8px rgba(0,0,0,0.3);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Click handler component to capture map clicks in pick mode
function MapClickPicker({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng); },
  });
  return null;
}

export default function FactoryMyMap() {
  const { user, logout, updateProfile } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [pickMode, setPickMode] = useState(false);
  const [pickedPos, setPickedPos] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadSDSInfo = async () => {
    try {
      const r = await axios.get(`${API}/sds`);
      let maxScore = 0, level = 'low', label = 'منخفض';
      let chemCount = 0;
      const sdsIds = [];
      (r.data || []).forEach((s) => {
        chemCount += s.chemicalsCount || 0;
        if (s.id) sdsIds.push(s.id);
        const sc = (s.risk?.score) || 0;
        if (sc > maxScore) {
          maxScore = sc;
          level = s.risk?.level || 'low';
          label = s.risk?.labelAr || 'منخفض';
        }
      });
      setInfo({
        riskScore: maxScore,
        riskLevel: level,
        riskLabel: label,
        chemicalsCount: chemCount,
        sdsIds,
        sdsCount: (r.data || []).length,
      });
    } catch (e) {
      // Optional SDS-summary call. Map still renders without it; log for debugging.
      console.warn('[FactoryMyMap] failed to load SDS info', e);
    }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSDSInfo(); }, []);

  const useGPS = () => {
    if (!navigator.geolocation) {
      toast.error('متصفحك لا يدعم تحديد الموقع');
      return;
    }
    toast.info('جارٍ تحديد موقعك...');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPickedPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setPickMode(true);
        toast.success('تم تحديد موقعك. يمكنك ضبطه بالنقر على الخريطة.');
      },
      (err) => {
        toast.error('تعذّر تحديد الموقع. تأكّد من السماح للمتصفح بالوصول للموقع.');
        // Still allow pick mode for manual selection
        setPickMode(true);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const saveLocation = async () => {
    if (!pickedPos) return;
    setSaving(true);
    try {
      await updateProfile({ lat: pickedPos.lat, lng: pickedPos.lng });
      toast.success('تم حفظ موقع المصنع');
      setPickMode(false);
      setPickedPos(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'تعذر حفظ الموقع');
    } finally {
      setSaving(false);
    }
  };

  const cancelPick = () => { setPickMode(false); setPickedPos(null); };

  const hasLocation = user && user.lat != null && user.lng != null;
  const center = pickedPos
    ? [pickedPos.lat, pickedPos.lng]
    : hasLocation ? [user.lat, user.lng] : [23.8859, 45.0792];
  const zoom = pickedPos || hasLocation ? 13 : 6;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" dir="rtl">
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ModonLogo size={44}/>
            <div>
              <h1 className="font-extrabold text-slate-900">موقع المصنع على الخريطة</h1>
              <p className="text-xs text-slate-500">{user?.factoryName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NavCluster homePath="/factory" testidPrefix="factory-map" />
            <Button size="sm" variant="outline" onClick={() => { logout(); nav('/login'); }} data-testid="factory-logout-btn">
              <LogOut size={14} className="ml-1" /> خروج
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-600 p-6"><Loader2 className="animate-spin" size={16}/> جارٍ التحميل...</div>
        ) : (
          <>
            <Card className="p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-extrabold text-lg text-slate-900 flex items-center gap-2">
                    <Building2 size={16} className="text-[#1a4d80]"/> {user?.factoryName}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {user?.activity} • <MapPin size={11} className="inline ml-1"/> {user?.city}{user?.address ? ` • ${user.address}` : ''}
                  </div>
                  {!hasLocation && !pickMode && (
                    <div className="mt-2 text-xs text-amber-700 bg-amber-50 rounded p-2 max-w-md">
                      <AlertCircle size={12} className="inline ml-1"/>
                      لم تُحدّد إحداثيات المصنع بعد. حدّدها لتظهر على خريطتك ولفِرَق الإطفاء.
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {(info?.sdsCount || 0) > 0 && (
                    <Badge variant="secondary"><FileText size={11} className="ml-1"/> {info.sdsCount} تقرير</Badge>
                  )}
                  {(info?.riskScore || 0) > 0 && (
                    <Badge style={{ backgroundColor: RISK_COLORS[info.riskLevel] || '#64748b', color: 'white' }}>
                      <ShieldAlert size={11} className="ml-1"/> {info.riskLabel} ({info.riskScore})
                    </Badge>
                  )}
                  {!pickMode ? (
                    <>
                      <Button size="sm" variant="outline" onClick={useGPS} data-testid="gps-locate-btn">
                        <Crosshair size={14} className="ml-1"/> تحديد بالـ GPS
                      </Button>
                      <Button size="sm" onClick={() => setPickMode(true)} className="bg-[#1a4d80] hover:bg-[#143d68]" data-testid="pick-on-map-btn">
                        <MapPin size={14} className="ml-1"/> {hasLocation ? 'تعديل الموقع' : 'تحديد على الخريطة'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={cancelPick} data-testid="cancel-pick-btn">
                        <X size={14} className="ml-1"/> إلغاء
                      </Button>
                      <Button size="sm" onClick={saveLocation} disabled={!pickedPos || saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-location-btn">
                        {saving ? <Loader2 size={14} className="animate-spin ml-1"/> : <Save size={14} className="ml-1"/>}
                        حفظ الموقع
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {pickMode && (
                <div className="mt-3 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900">
                  <Crosshair size={12} className="inline ml-1"/>
                  انقر على الخريطة في موقع مصنعك بالضبط، ثم اضغط "حفظ الموقع".
                  {pickedPos && (
                    <div className="mt-1 font-mono text-[11px]">
                      الإحداثيات: {pickedPos.lat.toFixed(6)}, {pickedPos.lng.toFixed(6)}
                    </div>
                  )}
                </div>
              )}
            </Card>

            <Card className="overflow-hidden" data-testid="my-map-card">
              <div style={{ height: '70vh', width: '100%' }}>
                <MapContainer center={center} zoom={zoom} scrollWheelZoom style={{ height: '100%', width: '100%', cursor: pickMode ? 'crosshair' : 'grab' }}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="قمر صناعي">
                      <TileLayer
                        attribution='Tiles © Esri'
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        maxZoom={19}
                      />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="شوارع (OSM)">
                      <TileLayer
                        attribution='© OpenStreetMap'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>
                  {pickMode && <MapClickPicker onPick={setPickedPos}/>}

                  {/* Existing saved location (visible unless picking) */}
                  {hasLocation && !pickMode && (
                    <CircleMarker
                      center={[user.lat, user.lng]}
                      radius={14}
                      pathOptions={{
                        color: RISK_COLORS[info?.riskLevel] || '#1a4d80',
                        fillColor: RISK_COLORS[info?.riskLevel] || '#1a4d80',
                        fillOpacity: 0.75,
                        weight: 3,
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -10]} permanent>
                        <div className="text-xs font-bold">{user.factoryName}</div>
                      </Tooltip>
                      <Popup minWidth={240}>
                        <div dir="rtl" style={{ direction: 'rtl', textAlign: 'right' }}>
                          <div className="font-bold text-slate-900 mb-1">{user.factoryName}</div>
                          <div className="text-xs text-slate-600 space-y-0.5">
                            <div>النشاط: {user.activity || '—'}</div>
                            <div>المدينة: {user.city || '—'}</div>
                            {user.address && <div>العنوان: {user.address}</div>}
                            {user.phone && <div className="flex items-center gap-1"><Phone size={11}/> {user.phone}</div>}
                          </div>
                          <div className="mt-2 text-[10px] text-slate-500 border-t pt-1">
                            أرقام الطوارئ: 998 / 997
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  )}

                  {/* Picking marker */}
                  {pickMode && pickedPos && (
                    <Marker position={[pickedPos.lat, pickedPos.lng]} icon={PICK_ICON}>
                      <Popup>الموقع المختار</Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
              {!hasLocation && !pickMode && (
                <div className="p-4 text-center bg-slate-50 border-t text-sm text-slate-600">
                  <Crosshair className="inline ml-1 text-[#1a4d80]" size={14}/>
                  اضغط على <b>"تحديد بالـ GPS"</b> أو <b>"تحديد على الخريطة"</b> لإضافة موقع مصنعك.
                </div>
              )}
            </Card>
          </>
        )}
      </main>
      <SiteFooter showPublicLinks={false}/>
    </div>
  );
}
